import React, {FC, useState, useMemo, useContext, useEffect, useRef} from 'react'
import {composeInitialProps, useTranslation} from 'react-i18next'
import axios from 'axios'
import {Fund} from '../types/Fund'
import {ILanguage} from '../types/common'
import {COLUMNS, filterRows, invRor, sortRows} from './Helpers'
import FilterCompared from '../FundCatalogApp/filters/FilterCompared'
import LineChart from './LineChart'
import {ChartData} from '../types/ChartData'
import {Periods} from '../types/Periods'
import VisibleColumnsSelectDropdown, {IVisibleColumnsOption} from './VisibleColumnsSelectDropdown'
import FundTableHeaderCell from './FundTableHeaderCell'
import ComparedFundTableRow from './ComparedFundTableRow'
import {GlobalContext} from './ctx/GlobalContext'
import SimpleBar from 'simplebar-react'
import {useTimeout} from 'usehooks-ts'
import {format, isSameDay} from 'date-fns'
import {Oval} from 'react-loader-spinner'
import usePrevious from '../hooks/usePrevious'
import SVGIcon from './SVGIcon'
import * as XLSX from 'xlsx-js-style'
import {rorListDownload} from './XlsxTools'

const ComparedFundTable: FC<{
    data: Array<Fund>
    ctx: any
    language: ILanguage
    dynamicColumns: object
}> = ({data, ctx, language, dynamicColumns}) => {
    const {t} = useTranslation()
    const globalCtx = useContext(GlobalContext)
    const [selectedRateOfReturnOptions, setSelectedRateOfReturnOptions] = useState<Array<string>>([])
    const [excluded, setExcluded] = useState<Array<string>>([])
    const rateOfReturnOptions: Array<IVisibleColumnsOption> = Object.entries(dynamicColumns).map(
        ([, item]): IVisibleColumnsOption => {
            return item as IVisibleColumnsOption
        }
    )
    const scrollTopRef = useRef(null)
    const scrollTopContentRef = useRef(null)
    const scrollBottomRef = useRef(null)
    const scrollBottomContentRef = useRef(null)
    const [scrollTopContentWidth, setScrollTopContentWidth] = useState(0)
    const previousDate: unknown = usePrevious(ctx.date)
    const previousToday: unknown = usePrevious(ctx.today)

    useTimeout(() => {
        handleResize()
    }, 500)

    const handleResize = () => {
        if (ctx.compared.length > 0) {
            const {width} = scrollBottomContentRef.current.getBoundingClientRect()
            setScrollTopContentWidth(width)
        }
    }

    useEffect(() => {
        if (ctx.compared.length > 0) {
            handleResize()

            window.addEventListener('resize', handleResize)
            scrollTopRef.current.recalculate()
            scrollBottomRef.current.recalculate()

            scrollTopRef.current
                .getScrollElement()
                .addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
                    scrollBottomRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
                })

            scrollBottomRef.current
                .getScrollElement()
                .addEventListener('scroll', (event: {target: {scrollLeft: number}}) => {
                    scrollTopRef.current.getScrollElement().scrollLeft = event.target.scrollLeft
                })

            return () => {
                window.removeEventListener('resize', handleResize)
                scrollTopRef.current.getScrollElement().removeEventListener('scroll', () => {})
                scrollBottomRef.current.getScrollElement().removeEventListener('scroll', () => {})
            }
        }
    })

    useEffect(() => {
        setSelectedRateOfReturnOptions(ctx.compareColumns)
    }, [ctx])

    useEffect(() => {
        if (ctx.compared.length > 0) {
            const ids = data.map((item: Fund) => {
                return item.id
            })

            const excluded: Array<string> = ctx.compared.filter((item: string) => {
                return !ids.includes(item)
            })

            ctx.setChartItems(() =>
                ctx.chartItems.filter((item: string) => {
                    return ids.includes(item)
                })
            )

            if (excluded) {
                setExcluded(excluded)
                ctx.setCompared((prev: Array<string>) => {
                    return [
                        ...prev.filter((item: string) => {
                            return ids.includes(item)
                        }),
                    ]
                })
            }
        }
    }, [data])

    const handleSelectedRateOfReturnColumns = (value: string): void => {
        ctx.setCompareColumns((prevSelected: Array<string>) => {
            const newArray = [...prevSelected]
            if (newArray.includes(value)) {
                return newArray.filter((item) => item != value)
            } else {
                newArray.push(value)
                return newArray
            }
        })
    }

    const [chartData, setChartData] = useState<ChartData>({
        begin: '',
        end: '',
        currency: '',
        relativePeriods: {} as Periods, // Initialize with appropriate value
        valuation_dates: [],
        data: [],
    })

    useEffect(() => {
        if (ctx.compared.length > 0) {
            const ids = data.map((item: Fund) => {
                return item.id
            })

            const compared: Array<string> = ctx.compared.filter((item: string) => {
                return ids.includes(item)
            })

            ctx.setChartItems(() =>
                ctx.chartItems.filter((item: string) => {
                    return compared.includes(item)
                })
            )

            axios
                .get<ChartData>(`${process.env.REACT_APP_API_BASE_URL}/api/fund/compare/${compared.join(',')}`, {
                    headers: {
                        Accept: 'application/json',
                    },
                    auth: {
                        username: `${process.env.REACT_APP_API_USERNAME}`,
                        password: `${process.env.REACT_APP_API_PASSWORD}`,
                    },
                })
                .then((result) => {
                    setChartData(result.data)
                })
                .catch((error) => {
                    console.error(error)
                })
        }
    }, [ctx.compared])

    const comparedRows = useMemo(() => filterRows(data, {id: ctx.compared}), [data, ctx.compared])
    const sortedComparedRows = useMemo(() => {
        return sortRows(
            comparedRows,
            {
                sortDir: ctx.sortCompareDir,
                sortCol: ctx.sortCompareCol,
            },
            language,
            ctx.favorites
        )
    }, [comparedRows, ctx.favorites, {sortDir: ctx.sortCompareDir, sortCol: ctx.sortCompareCol}])

    function handleSelectedRateOfReturnColumnsReset() {
        ctx.setCompareColumns(COLUMNS)
    }

    const handleDataSort = (sortCol: string): void => {
        if (sortCol !== ctx.sortCompareCol) {
            ctx.setSortCompareCol(sortCol)
            ctx.setSortCompareDir('ASC')
            return
        }

        if (sortCol === ctx.sortCompareCol && ctx.sortCompareDir === 'ASC') {
            ctx.setSortCompareCol(sortCol)
            ctx.setSortCompareDir('DESC')
            return
        }

        if (sortCol === ctx.sortCompareCol && ctx.sortCompareDir === 'DESC') {
            ctx.setSortCompareCol('name')
            ctx.setSortCompareDir('ASC')
            return
        }
    }

    function isLoader(): boolean {
        // ALL-74 - current valuations loader fix
        if (ctx.date === null && previousToday !== null && previousToday !== undefined) {
            return !(previousDate === null)
        }

        return !(!ctx.date || isSameDay(ctx.today, ctx.date))
    }

    const downloadCompareRor = () => {
        const fileDate = new Date()
        const headerTitle = t('label.xlsx.compare.title.' + globalCtx.layout)
        const filename =
            'Allianz.pl - ' + t('label.xlsx.list.filename.' + globalCtx.layout) + ' - ' + fileDate.getTime() + '.xlsx'

        rorListDownload(
            sortedComparedRows,
            selectedRateOfReturnOptions,
            t,
            globalCtx.layout,
            headerTitle,
            filename,
            fileDate
        )
    }

    return (
        <>
            {ctx.compared.length === 0 && (
                <div className="no-results">
                    <p>{t('label.compare.no-results.p1')}</p>
                    <p>{t('label.compare.no-results.p2')}</p>
                </div>
            )}
            {ctx.compared.length > 0 && (
                <div id={'compareContainer'}>
                    <FilterCompared language={language} ctx={ctx} excluded={excluded} />
                    <div id="tableCompare" className="fund-table" style={{marginBottom: '30px'}}>
                        <div className="results-sort display-none flex">
                            <div className="results-funds flex"></div>
                            <VisibleColumnsSelectDropdown
                                options={rateOfReturnOptions}
                                selected={selectedRateOfReturnOptions}
                                toggleOption={handleSelectedRateOfReturnColumns}
                                handleReset={handleSelectedRateOfReturnColumnsReset}
                            />
                        </div>
                        {isLoader() && (
                            <>
                                <Oval
                                    visible={true}
                                    height="44"
                                    width="42"
                                    color="#007AB3"
                                    ariaLabel="oval-loading"
                                    wrapperStyle={{}}
                                    wrapperClass="spiner spiner-compared"
                                />
                            </>
                        )}
                        <div className="move-scrollbar"> {t('label.scroll.more')}</div>
                        <SimpleBar
                            autoHide={false}
                            forceVisible={false}
                            scrollbarMinSize={0}
                            scrollbarMaxSize={1118}
                            ref={scrollTopRef}>
                            <div
                                className="scrollbar-top"
                                style={{width: scrollTopContentWidth}}
                                ref={scrollTopContentRef}></div>
                        </SimpleBar>

                        <SimpleBar
                            autoHide={false}
                            forceVisible={false}
                            scrollbarMinSize={0}
                            scrollbarMaxSize={1118}
                            ref={scrollBottomRef}>
                            <div className="fund-table-all" style={{borderBottom: 'none'}}>
                                <div className="fund-table-row display-none flex">
                                    <div className="fund-wrapper" ref={scrollBottomContentRef}>
                                        <div className="row-sort row-wrapper-border row-sort-compared">
                                            <div
                                                className="checkbox checkbox-dfe checkbox-compared flex"
                                                style={{fontWeight: '600'}}></div>
                                            <div className="results-sort-rwd results-sort-compared">
                                                <div className="results-funds flex">
                                                    <span className="selected-compared-fund">
                                                        Wybrane fundusze do porównania
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="row-price flex">
                                                <FundTableHeaderCell
                                                    handleDataSort={handleDataSort}
                                                    val="valuation"
                                                    label={t('label.column.valuation')}
                                                    className={
                                                        'sort-value flex ' +
                                                        (globalCtx.layout === 'ufk'
                                                            ? 'sort-value-ufk '
                                                            : 'sort-value-fio')
                                                    }
                                                    ctxSortCol={ctx.sortCompareCol}
                                                    ctxSortDir={ctx.sortCompareDir}
                                                />
                                                {globalCtx.layout === 'fio' && (
                                                    <FundTableHeaderCell
                                                        handleDataSort={handleDataSort}
                                                        val="sri"
                                                        label="SRI"
                                                        className="sort-period flex"
                                                        ctxSortCol={ctx.sortCompareCol}
                                                        ctxSortDir={ctx.sortCompareDir}
                                                    />
                                                )}

                                                {Object.entries(dynamicColumns).map(([key, item], index: number) => {
                                                    if (selectedRateOfReturnOptions.includes(item.value)) {
                                                        return (
                                                            <FundTableHeaderCell
                                                                key={index}
                                                                handleDataSort={handleDataSort}
                                                                val={key}
                                                                label={item.label}
                                                                className={
                                                                    'sort-period flex ' +
                                                                    (globalCtx.layout === 'ofe' ||
                                                                    globalCtx.layout === 'dfe'
                                                                        ? ' sort-period-ofe'
                                                                        : '') +
                                                                    (globalCtx.layout !== 'fio'
                                                                        ? ' sort-period-all'
                                                                        : '')
                                                                }
                                                                ctxSortCol={ctx.sortCompareCol}
                                                                ctxSortDir={ctx.sortCompareDir}
                                                            />
                                                        )
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isLoader() && (
                                    <>
                                        <div className="loader loader-compared"></div>
                                        {/*<Oval*/}
                                        {/*    visible={true}*/}
                                        {/*    height="44"*/}
                                        {/*    width="42"*/}
                                        {/*    color="#007AB3"*/}
                                        {/*    ariaLabel="oval-loading"*/}
                                        {/*    wrapperStyle={{}}*/}
                                        {/*    wrapperClass="spiner spiner-compared"*/}
                                        {/*/>*/}
                                    </>
                                )}

                                {sortedComparedRows.map((_fund: Fund, index: number) => {
                                    return (
                                        <ComparedFundTableRow key={'cidx_' + index.toString()} fund={_fund} ctx={ctx} />
                                    )
                                })}
                            </div>
                        </SimpleBar>
                        <div className="download download-compared flex">
                            <button
                                className="download-btn flex"
                                style={{alignItems: 'flex-end'}}
                                onClick={downloadCompareRor}>
                                <span className="download-icon" style={{gap: '8px'}}>
                                    <span className="download-text download-text-xlsx">
                                        {t('label.btn.xlsx.download')}
                                    </span>
                                    <SVGIcon
                                        id={'download-pdf'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                </span>
                            </button>
                        </div>
                    </div>

                    {chartData.data?.length > 0 && (
                        <LineChart
                            chartData={chartData}
                            language={language}
                            chartItems={ctx.chartItems}
                            setChartItems={ctx.setChartItems}
                            chartMode="compare"
                            ctx={ctx}
                            sortedComparedRows={sortedComparedRows}
                            selectedRateOfReturnOptions={selectedRateOfReturnOptions}
                        />
                    )}
                </div>
            )}
        </>
    )
}

export default ComparedFundTable
